import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/state/store/auth';
import { useGlobalQuery } from '@/state/query-client/hooks';
import {
  getEmployeeLeavePermissions,
  getLeaveSheet,
  getLeaveSheetTerms,
  leaveSheetPolicyOf,
} from '../services/leave-sheet.service';
import { LeaveSheetPolicy } from '../types/leave-sheet.types';

const POLICY_STALE_TIME = 5 * 60 * 1000;

/**
 * The leave rules that shape the employee's sheet. The signed-in user's own come from their token;
 * anybody else's from their roles in IAM. `policy` is `undefined` while that is loading.
 */
export const useLeaveSheetPolicy = (userId: string) => {
  const currentUserId = useAuthStore((state) => state.user?.itemId);
  const ownPermissions = useAuthStore((state) => state.user?.permissions) as string[] | undefined;
  const organizationId = useAuthStore((state) => state.selectedOrgId) ?? undefined;
  const isSelf = Boolean(userId) && userId === currentUserId;

  const { data: employeePermissions, error } = useQuery({
    queryKey: ['iam', 'employee-leave-permissions', userId, organizationId],
    queryFn: () => getEmployeeLeavePermissions(userId, organizationId),
    enabled: Boolean(userId) && !isSelf,
    staleTime: POLICY_STALE_TIME,
    retry: false,
  });

  const policy = useMemo<LeaveSheetPolicy | undefined>(() => {
    const held = isSelf ? ownPermissions : employeePermissions;
    if (!held) return undefined;
    const set = new Set(held);
    return leaveSheetPolicyOf((permission) => set.has(permission));
  }, [isSelf, ownPermissions, employeePermissions]);

  return { policy, error: isSelf ? null : error };
};

export const useGetLeaveSheetTerms = (userId: string, enabled = true) =>
  useGlobalQuery({
    queryKey: ['hr-leave-sheet-terms', userId],
    queryFn: () => getLeaveSheetTerms(userId),
    enabled: Boolean(userId) && enabled,
    staleTime: 60 * 1000,
  });

export const useGetLeaveSheet = (
  userId: string,
  termId: string | undefined,
  policy: LeaveSheetPolicy | undefined
) =>
  useGlobalQuery({
    queryKey: ['hr-leave-sheet', userId, termId ?? 'current', policy],
    queryFn: () => getLeaveSheet({ userId, termId, policy: policy as LeaveSheetPolicy }),
    enabled: Boolean(userId) && Boolean(policy),
    staleTime: 60 * 1000,
  });
